import React from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { MediaCardContainer, MediaDisplay } from "@/components/MediaCard";
import { cn } from "@/utils/cn";

interface PostMediaSectionProps {
  mediaItem: MediaItem;
  viewMode?: "grid" | "list" | "feed";
  isReel?: boolean;
  isCarousel?: boolean;
  carouselIndex?: number;
  carouselTotal?: number;
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
  children?: React.ReactNode;
  autoPlay?: boolean;
}

export const PostMediaSection: React.FC<PostMediaSectionProps> = ({
  mediaItem,
  viewMode = "grid",
  isReel = false,
  isCarousel = false,
  carouselIndex,
  carouselTotal,
  onClick,
  className,
  children,
  autoPlay = false,
}) => {
  // List view - small thumbnail
  if (viewMode === "list") {
    return (
      <div
        className={cn("w-12 h-12 rounded-lg shrink-0 overflow-hidden", className)}
        onClick={onClick}
      >
        <MediaCardContainer aspectRatio="1:1">
          <MediaDisplay mediaItem={mediaItem} useThumbnail={true} showPlayButton={false} onClick={onClick} />
          {children}
        </MediaCardContainer>
      </div>
    );
  }

  // Feed view - full 9:16 for reels, 4:5 for others
  if (viewMode === "feed") {
    const aspectRatioClass = isReel ? "aspect-9/16" : "aspect-4/5";

    // Auto-play inline video for any video in feed mode
    if (autoPlay && mediaItem.isVideo()) {
      return (
        <div className={cn("relative overflow-hidden bg-black", className)}>
          <div className={aspectRatioClass}>
            <div className="w-full h-full relative">
              <MediaDisplay
                mediaItem={mediaItem}
                autoPlay
                className="w-full h-full"
              />
              {children}
            </div>
          </div>
        </div>
      );
    }

    // Default feed view with thumbnail
    return (
      <div className={cn("relative overflow-hidden", className)}>
        <div className={aspectRatioClass}>
          <div
            onClick={onClick}
            className={cn(
              "w-full h-full",
              onClick && isReel && "cursor-pointer group"
            )}
          >
            <MediaCardContainer aspectRatio={isReel ? "9:16" : "4:5"}>
              <MediaDisplay mediaItem={mediaItem} useThumbnail={true} showPlayButton={isReel} onClick={onClick} />
              {children}
              {/* Visual feedback for clickable reels */}
              {onClick && isReel && (
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 flex items-center justify-center">
                  <div className="transform scale-100 group-hover:scale-110 transition-transform duration-200 opacity-0 group-hover:opacity-100">
                    <div className="w-16 h-16 rounded-full bg-white bg-opacity-90 flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-800 ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                </div>
              )}
            </MediaCardContainer>
          </div>
        </div>
      </div>
    );
  }

  // Grid view - full size
  return (
    <div className={cn("relative overflow-hidden rounded-t-xl", className)}>
      <div className="aspect-4/5">
        <div
          onClick={onClick}
          className={cn(
            "w-full h-full",
            onClick && "cursor-pointer hover:opacity-90 transition-opacity duration-200"
          )}
        >
          {isReel ? (
            // For reels, render a larger thumbnail and crop to 4:5
            <div className="w-full h-full relative">
              <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
                <div className="w-full">
                  <MediaCardContainer aspectRatio="9:16">
                    <MediaDisplay mediaItem={mediaItem} useThumbnail={true} showPlayButton={true} onClick={onClick} />
                  </MediaCardContainer>
                </div>
              </div>
              {children}
            </div>
          ) : (
            <MediaCardContainer aspectRatio="4:5">
              <MediaDisplay mediaItem={mediaItem} useThumbnail={true} showPlayButton={mediaItem.isVideo()} onClick={onClick} />
              {children}
            </MediaCardContainer>
          )}
        </div>
      </div>
    </div>
  );
};
